import React from 'react';

// TypeScript interfaces for ProfileCompleteness
interface Profile {
  id?: number;
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  zip?: string;
  city?: string;
  state?: string;
  street_address?: string;
  street_address_2?: string;
  background?: string;
  avatar_url?: string;
  resume?: string;
  resume_file_name?: string;
  experiences?: any[];
  educations?: any[];
  skills?: any[];
  job_types?: any[];
  position_type_ids?: any[];
  hiring_types?: any[];
  [key: string]: any;
}

interface ProfileField {
  field: any;
  value: number;
  name: string;
  section: string;
}

/**
 * Check profile fields for completeness calculation
 */
function checkProfileFields(profile: Profile): ProfileField[] {
  return [
    { field: profile.first_name, value: 5, name: "First Name", section: "1" },
    { field: profile.last_name, value: 5, name: "Last Name", section: "1" },
    { field: profile.phone, value: 5, name: "Phone", section: "1" },
    { field: profile.zip, value: 5, name: "Zip Code", section: "1" },
    { field: profile.city, value: 3, name: "City", section: "1" },
    { field: profile.state, value: 3, name: "State", section: "1" },
    { field: profile.street_address, value: 3, name: "Street Address", section: "1" },
    { field: profile.background, value: 10, name: "About Me", section: "1" },
    { field: profile.avatar_url, value: 10, name: "Profile Photo", section: "1" },
    { field: profile.resume_file_name, value: 15, name: "Resume", section: "2" },
    { field: profile.experiences, value: 15, name: "Experience", section: "3" },
    { field: profile.educations, value: 10, name: "Education", section: "3" },
    { field: profile.skills, value: 15, name: "Skills", section: "4" },
    { field: profile.job_types, value: 10, name: "Job Types", section: "4" },
    { field: profile.position_type_ids, value: 5, name: "Hiring Availability", section: "4" },
  ];
}

interface ProfileCompletenessProps {
  profile: Profile;
  className?: string;
  showDetails?: boolean;
}

/**
 * ProfileCompleteness Component - Enhanced TypeScript version
 * Displays profile completion percentage with progress bar
 * Helps users understand what fields they need to complete
 */
export const ProfileCompleteness: React.FC<ProfileCompletenessProps> = ({
  profile,
  className = '',
  showDetails = true,
}) => {
  const profileFields = checkProfileFields(profile);

  // Calculate completion percentage
  const missingPoints = profileFields.reduce((acc, field) => {
    // Check if field is empty (null, undefined, empty string, or empty array)
    const isEmpty = Array.isArray(field.field)
      ? field.field.length === 0
      : !field.field || field.field === '';

    return isEmpty ? acc + field.value : acc;
  }, 0);

  const percentage = Math.max(0, 100 - missingPoints);

  // Get missing fields for details view - always calculate this
  const missingFields = profileFields.filter(field => {
    const isEmpty = Array.isArray(field.field)
      ? field.field.length === 0
      : !field.field || field.field === '';
    return isEmpty;
  });

  // Check if this is the integrated view
  const isIntegrated = className.includes('integrated-completeness') || className.includes('integrated-below-tabs');

  return (
    <div
      className={`profile-progress ${isIntegrated ? '' : 'form-section-card'} ${className}`.trim()}
      style={isIntegrated ? {} : {
        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
        marginBottom: '2rem'
      }}
    >
      <div className={`d-flex align-items-center justify-content-between ${isIntegrated ? 'mb-2' : 'mb-3'}`}>
        <h4 className="mb-0" style={{
          color: '#034ba6',
          fontWeight: 600,
          fontSize: isIntegrated ? '1.1rem' : '1.5rem'
        }}>
          <i className="fas fa-chart-line me-2" style={{ color: '#085ec8' }} aria-hidden="true"></i>
          Profile Completeness
        </h4>
        <span
          className={`badge ${isIntegrated ? 'fs-6' : 'fs-5'}`}
          style={{
            background: percentage >= 90
              ? 'linear-gradient(135deg, #71ad08 0%, #5e8f07 100%)'
              : percentage >= 70
              ? 'linear-gradient(135deg, #085ec8 0%, #176fc9 100%)'
              : 'linear-gradient(135deg, #fe6718 0%, #ff8040 100%)',
            padding: isIntegrated ? '0.4rem 0.8rem' : '0.5rem 1rem',
            borderRadius: '50px',
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          {percentage}%
        </span>
      </div>

      <div className="progress-bar-container" style={{
        height: '12px',
        background: '#e2e8f0',
        borderRadius: '50px',
        overflow: 'hidden',
        marginBottom: '1rem'
      }}>
        <div
          className="progress-bar"
          style={{
            width: `${percentage}%`,
            height: '100%',
            background: percentage >= 90
              ? 'linear-gradient(135deg, #71ad08 0%, #5e8f07 100%)'
              : percentage >= 70
              ? 'linear-gradient(135deg, #085ec8 0%, #176fc9 100%)'
              : 'linear-gradient(135deg, #fe6718 0%, #ff8040 100%)',
            borderRadius: '50px',
            transition: 'width 0.5s ease',
            position: 'relative'
          }}
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Profile ${percentage}% complete`}
        >
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
            animation: 'shimmer 2s infinite'
          }}></div>
        </div>
      </div>

      {!isIntegrated && (
        <p className="progress-text" style={{
          color: '#4a5568',
          fontSize: '0.95rem',
          marginBottom: 0,
          fontWeight: 500
        }}>
          {percentage >= 90 ? (
            <>
              <i className="fas fa-check-circle me-2" style={{ color: '#71ad08' }} aria-hidden="true"></i>
              Excellent! Your profile is nearly complete.
            </>
          ) : percentage >= 70 ? (
            <>
              <i className="fas fa-thumbs-up me-2" style={{ color: '#085ec8' }} aria-hidden="true"></i>
              Good progress! A few more details will help you get more offers.
            </>
          ) : (
            <>
              <i className="fas fa-exclamation-circle me-2" style={{ color: '#fe6718' }} aria-hidden="true"></i>
              A more complete profile will help you get more job offers.
            </>
          )}
        </p>
      )}

      {/* Optional details section */}
      {showDetails && missingFields.length > 0 && !isIntegrated && (
        <div className="mt-3">
          <h6 className="text-muted">Missing Information:</h6>
          <div className="row">
            {missingFields.map((field, index) => (
              <div key={index} className="col-md-6 col-lg-4 mb-2">
                <small className="text-muted">
                  <i className="fas fa-minus-circle me-1" aria-hidden="true"></i>
                  {field.name} ({field.value} pts)
                </small>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileCompleteness;
